import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/public/SiteFooter";
import SmartLink from "@/components/public/SmartLink";
import { useSite } from "@/context/SiteContext";
import { SectionHead, ICONS } from "@/components/landing/HeroSections";
import { Faq } from "@/components/landing/MoreSections";
import { ArrowRight, CheckCircle2, Sparkles, Truck } from "lucide-react";
import DriverAreaMap from "@/components/DriverAreaMap";

const fadeUp = (i = 0) => ({ initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5, delay: i * 0.07 } });

function PillLink({ href, children, primary, testid }) {
  return (
    <SmartLink href={href} data-testid={testid}
      className={`group inline-flex h-12 items-center gap-2 rounded-full px-7 font-bold transition-[background-color,transform] hover:-translate-y-0.5 ${primary ? "bg-brand text-white shadow-lg shadow-brand/25 hover:bg-brand-hover" : "border border-white/25 text-white hover:bg-white/10"}`}>
      {children} {primary && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
    </SmartLink>
  );
}

export function DHero({ d, tid = "drivers", Icon = Truck }) {
  const h = d.hero || {};
  return (
    <section className="hero-glow relative -mt-[72px] overflow-hidden pt-[72px] text-white" data-testid={`${tid}-hero`}>
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 lg:grid-cols-12 lg:px-8 lg:py-24">
        <motion.div {...fadeUp()} className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80"><Icon className="h-3.5 w-3.5 text-brand-orange" /> {h.overline}</span>
          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid={`${tid}-hero-title`}>{h.title}<br /><span className="text-brand-orange">{h.title_accent}</span></h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-white/70 md:text-lg">{h.subtitle}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <PillLink href={h.cta_href || "/register"} primary testid={`${tid}-apply-btn`}>{h.cta_btn}</PillLink>
            {h.secondary_btn && <PillLink href={h.secondary_href || `/${tid}#steps`} testid={`${tid}-how-btn`}>{h.secondary_btn}</PillLink>}
          </div>
        </motion.div>
        {h.image && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative lg:col-span-6">
            <div className="absolute -inset-6 rounded-[40px] bg-brand/20 blur-3xl" />
            <img src={h.image} alt="Dashboard" className="relative w-full rounded-[28px] shadow-2xl ring-1 ring-white/15" data-testid={`${tid}-hero-image`} />
          </motion.div>
        )}
      </div>
    </section>
  );
}

export function Benefits({ d, tid = "drivers" }) {
  const b = d.benefits || {};
  return (
    <section className="bg-white" data-testid={`${tid}-benefits`}>
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
        <SectionHead overline={b.overline} heading={b.heading} />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {(b.items || []).map((it, i) => {
            const Icon = ICONS[it.icon] || Sparkles;
            return (
              <motion.div key={i} {...fadeUp(i)} className="group rounded-3xl border border-slate-200 p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`${tid}-benefit-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-brand transition-transform group-hover:-rotate-6"><Icon className="h-5 w-5" /></span>
                <h3 className="mt-5 font-heading text-lg font-bold text-slate-900">{it.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-500">{it.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Requirements({ d }) {
  const r = d.requirements || {};
  return (
    <section className="bg-slate-50" data-testid="drivers-requirements">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-12 lg:px-8 lg:py-24">
        <div className="lg:col-span-5"><SectionHead overline={r.overline} heading={r.heading} sub={r.subheading} /></div>
        <ul className="grid gap-3 sm:grid-cols-2 lg:col-span-7">
          {(r.items || []).map((t, i) => (
            <motion.li key={i} {...fadeUp(i)} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-[15px] font-medium text-slate-700" data-testid={`drivers-req-${i}`}>
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" /> {t}
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function Steps({ d, tid = "drivers" }) {
  const s = d.steps || {};
  return (
    <section id="steps" className="scroll-mt-24 bg-white" data-testid={`${tid}-steps`}>
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <SectionHead overline={s.overline} heading={s.heading} />
        <div className="mt-14 space-y-16 lg:space-y-24">
          {(s.items || []).map((it, i) => (
            <motion.div key={i} {...fadeUp()} className="grid items-center gap-8 lg:grid-cols-12 lg:gap-14" data-testid={`${tid}-step-${i}`}>
              <div className={`lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand font-heading text-lg font-extrabold text-white shadow-lg shadow-brand/25">{i + 1}</span>
                <h3 className="mt-5 font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{it.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-slate-500 md:text-lg">{it.body}</p>
              </div>
              {it.image && (
                <div className={`lg:col-span-7 ${i % 2 ? "lg:order-1" : ""}`}>
                  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 p-2 shadow-xl shadow-slate-900/5">
                    <img src={it.image} alt={it.title} className="w-full rounded-2xl" loading="lazy" data-testid={`${tid}-step-img-${i}`} />
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DCta({ d, tid = "drivers" }) {
  const t = d.cta || {};
  return (
    <section className="bg-white px-5 pb-20 lg:px-8 lg:pb-28" data-testid={`${tid}-cta`}>
      <div className="hero-glow relative mx-auto max-w-7xl overflow-hidden rounded-[36px] px-8 py-16 text-white lg:px-16">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">{t.title}</h2>
        <p className="mt-4 max-w-xl text-white/70">{t.subtitle}</p>
        <div className="mt-8"><PillLink href={t.href || "/register"} primary testid={`${tid}-cta-apply-btn`}>{t.btn}</PillLink></div>
      </div>
    </section>
  );
}

const SECTIONS = { hero: DHero, benefits: Benefits, requirements: Requirements, steps: Steps, map: DriverAreaMap, faq: ({ d }) => <Faq c={{ faq: d.faq }} />, cta: DCta };

export default function DriversPage() {
  const { content } = useSite();
  const d = content?.drivers_page;
  if (!d) return <div className="public-site min-h-screen bg-navy"><Navbar dark /></div>;
  const visible = (d.sections || []).filter((s) => s.enabled && SECTIONS[s.id]);
  return (
    <div className="public-site min-h-screen bg-white" data-testid="drivers-page">
      <Navbar dark={visible[0]?.id === "hero"} />
      {visible.map((s) => { const Comp = SECTIONS[s.id]; return <Comp key={s.id} d={d} />; })}
      <SiteFooter />
    </div>
  );
}
